import { useCallback, useEffect, useRef, useState } from 'react';
import { META_OPTIMIZER_ENABLED } from '../utils/portfolioDesign';
import { callBackend } from '../utils/api';
import { runOptimizerWithQueue } from '../utils/optimizerQueue';
import { parseCloudRunError } from '../components/msiDriftReviewHelpers';
import { normalizeCogniveId } from '../components/optimizer/optimizerIdentityHelpers';
import { expectedRunProvider } from '../utils/optimizerObjectives';
import { createOptimizerOperationId } from '../utils/optimizerConstraintEditor';
import { requireOptimizerV2RunResponse } from '../utils/optimizerGenericGroupContract';
import { buildOptimizerRunCommand, filterGroupConstraintsToCatalog, optimizerBaselineForViewMode,
    optimizerRunPreflightError } from '../utils/optimizerRunCommand';

const EXPECTED_OPTIMIZER_OUTCOMES = Object.freeze({
    optimization_infeasible: Object.freeze({
        status: 'INFEASIBLE',
        message: 'The optimizer constraints are too tight. Relax one or more constraints and rerun.',
    }),
    optimization_unbounded: Object.freeze({
        status: 'UNBOUNDED',
        message: 'The optimizer objective is unbounded. Check or tighten the GMV/global portfolio limit and rerun.',
    }),
    solver_no_solution_before_limit: Object.freeze({
        status: 'NO_SOLUTION_BEFORE_LIMIT',
        message: 'No feasible portfolio was found before the mixed-integer time limit. This is not proof that the constraints are infeasible.',
    }),
});

// The workspace owns freshness and accepted results. This hook owns the one
// queue/cancellation channel shared by ordinary runs and saved reoptimization.
export default function useOptimizerExecution(userScopeKey) {
    const [queueState, setQueueState] = useState(null);
    const controllerRef = useRef(null);
    useEffect(() => {
        setQueueState(null);
        return () => controllerRef.current?.abort();
    }, [userScopeKey]);
    const cancel = useCallback(() => controllerRef.current?.abort(), []);
    const clear = useCallback(() => {
        controllerRef.current?.abort();
        setQueueState(null);
    }, []);
    const runCommand = useCallback((command, { isCurrent }) => {
        const controller = new AbortController();
        controllerRef.current = controller;
        return runOptimizerWithQueue(command, {
            isCurrent,
            onWaiting: (state) => { if (isCurrent()) setQueueState(state); },
            signal: controller.signal,
        });
    }, []);

    const runOptimization = useCallback(async ({ operations, workspace, results, feedback, experiment }) => {
        if (results.currentUnsavedRun && results.completedSourceKind !== 'meta') {
            feedback.error(
                'Results are up to date. Change the configuration or start a new run.',
            );
            return;
        }
        const { operation, runGeneration } = operations.beginRun();
        // The owner keeps the displayed receipt while this attempt runs. A
        // failure can therefore reveal the old result after inputs are reverted.
        const { runInputRows, runInputIdentity, viewMode, constraints, constraintToggles,
            groupConstraints, objective, posSizingOptimizerContext } = workspace.captureRun();
        operations.bindRunIdentity(runInputIdentity);
        const isCurrentRun = () => operations.ownsRun(operation, runGeneration, runInputIdentity);
        const failCurrentRun = (message, status = 'ERROR') => {
            if (!isCurrentRun()) return;
            feedback.fail(message, status);
        };

        const usesSavedPosSizingContext = viewMode === 'pos-sizing';
        const savedContextDigest = usesSavedPosSizingContext
            ? posSizingOptimizerContext?.context_document_sha256
            : null;

        feedback.begin();

        try {
            const { capability, catalog } = await workspace.resolveContract();
            if (!isCurrentRun()) return;
            const selectedCogniveIds = runInputRows
                .filter((row) => row?.checkBoxEnable === true)
                .map((row) => normalizeCogniveId(row?.cognive_id))
                .filter(Boolean)
                .sort();
            const configuration = {
                baselineSourceMode: optimizerBaselineForViewMode(viewMode),
                selectedCogniveIds,
                constraints,
                constraintToggles,
                groupConstraints: filterGroupConstraintsToCatalog(groupConstraints, catalog),
                objective,
            };
            const preflightError = optimizerRunPreflightError({
                ...configuration,
                capability,
                posSizingOptimizerContext,
            });
            if (preflightError) throw new Error(preflightError);
            const command = buildOptimizerRunCommand({
                ...configuration,
                posSizingContextDocumentSha256: savedContextDigest,
                runOperationId: createOptimizerOperationId(),
            });
            if (META_OPTIMIZER_ENABLED) {
                await callBackend('optimizer_design/current', 'DELETE');
                if (!isCurrentRun()) return;
                experiment.started();
            }
            const rawResponse = await runCommand(command, { isCurrent: isCurrentRun });
            const expectedResponse = {
                runOperationId: command.run_operation_id,
                objective: command.objective,
                provider: expectedRunProvider(command, capability.provider),
            };
            const receiptResponse = requireOptimizerV2RunResponse(rawResponse, expectedResponse);
            if (!isCurrentRun()) return;
            results.publishRun({
                receiptResponse,
                runInputRows,
                runInputIdentity,
                runGeneration,
                posSizingContextDigest: usesSavedPosSizingContext
                    ? savedContextDigest
                    : null,
            });
        } catch (err) {
            if (!isCurrentRun()) return;
            const parsedError = parseCloudRunError(err);
            const expectedOutcome = EXPECTED_OPTIMIZER_OUTCOMES[parsedError.code];
            if (expectedOutcome) {
                failCurrentRun(expectedOutcome.message, expectedOutcome.status);
                return;
            }
            console.error('runOptimization unexpected error:', err);
            if (
                usesSavedPosSizingContext
                && parsedError.code === 'optimizer_position_sizing_context_changed'
            ) {
                void workspace.onPosSizingContextChanged?.();
            }
            failCurrentRun(
                parsedError.message || 'An unexpected error occurred during optimization.',
                err?.name === 'AbortError' ? 'CANCELLED' : 'ERROR',
            );
        } finally {
            if (isCurrentRun()) {
                feedback.finish();
            }
        }
    }, [runCommand]);
    return { queueState, cancel, clear, runCommand, runOptimization };
}
